import {
  Box,
  Button,
  Grid2 as Grid,
  Stack,
  TextField,
  Divider,
  InputAdornment,
  Alert,
  Paper,
  TableContainer,
  TableCell,
  TableRow,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { useState } from 'react'
import { DicomTagMetadata, DicomTagRecord } from '@common/config/api/client'
import * as React from 'react'
import VirtualizedTable from '@common/components/VirtualizedTable'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import DICOMTagDeidRow from '@study_setup/components/DICOMTagsDeId/DICOMTagDeidRow'
import CreateStudyTagDeidDialog from '@study_setup/components/DICOMTagsDeId/CreateStudyTagDeidDialog'
import { useFormContext } from 'react-hook-form'
import { DicomTagsDeidValues } from '../types'

interface TagDeidManagementTableProps {
  error: Error | null
}

const TagDeidManagementTable = ({ error }: TagDeidManagementTableProps) => {
  const [collapseStates, setCollapseStates] = useState<{ [key: number]: boolean }>({})
  const [createStudyTagDeidDialogOpen, setCreateStudyTagDeidDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const { watch } = useFormContext<DicomTagsDeidValues>()
  const tags = watch('tags')

  const filteredTags = React.useMemo(() => {
    if (!searchTerm) return tags
    const lowerSearchTerm = searchTerm.toLowerCase()
    return tags?.filter(
      (tag: DicomTagRecord | DicomTagMetadata) =>
        tag.tag_name.toLowerCase().includes(lowerSearchTerm) || tag.tag_id.toLowerCase().includes(lowerSearchTerm),
    )
  }, [tags, searchTerm])

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value),
    [],
  )

  const toggleCollapse = (index: number) => {
    setCollapseStates((prevStates) => ({
      ...prevStates,
      [index]: !prevStates[index],
    }))
  }

  const TableHeader = () => (
    <TableRow sx={{ bgcolor: 'paper.main', width: '100%' }}>
      <TableCell sx={{ width: 50 }} />
      <TableCell>Tag ID and Name</TableCell>
      <TableCell>Tag Status</TableCell>
      <TableCell>Tag Inclusion</TableCell>
      <TableCell>Modification</TableCell>
      <TableCell>Apply to all</TableCell>
      <TableCell sx={{ width: 150 }} />
    </TableRow>
  )
  const VirtuosoTableRow = (index: number, tag: DicomTagRecord) => {
    return (
      <React.Fragment>
        <DICOMTagDeidRow
          tagId={tag.tag_id}
          isOpen={collapseStates[index] || false}
          onToggleCollapse={() => toggleCollapse(index)}
          isGlobalTagDeidView={true}
        />
      </React.Fragment>
    )
  }

  return (
    <Grid container rowSpacing={SPACING.spacingLg}>
      <Grid size={SIZING.gridSizeXxl} offset={{ xs: 0, lg: 0 }}>
        <Stack spacing={SPACING.spacingLg}>
          <>
            {error && (
              <Alert severity="error" sx={{ mt: SPACING.spacingLg }}>
                Error loading DICOM tags: {error.message}
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ mt: SPACING.spacingXxl, padding: SPACING.spacingLg }}>
              <Box display="flex" justifyContent="flex-end" gap={SPACING.spacingLg} py={SPACING.spacingLg}>
                <Button variant="contained" color="secondary" onClick={() => setCreateStudyTagDeidDialogOpen(true)}>
                  Add DICOM tag
                </Button>
                <Divider orientation="vertical" flexItem />
                <TextField
                  size="small"
                  variant="outlined"
                  label="Search tags"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
              {filteredTags && filteredTags.length > 0 && (
                <VirtualizedTable
                  data={filteredTags as DicomTagRecord[]}
                  expandedRows={collapseStates}
                  headerContent={TableHeader}
                  rowContent={VirtuosoTableRow}
                />
              )}
            </TableContainer>
            {createStudyTagDeidDialogOpen && (
              <CreateStudyTagDeidDialog
                isTagDeidGlobalView={true}
                onClose={() => setCreateStudyTagDeidDialogOpen(false)}
              />
            )}
          </>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default TagDeidManagementTable
